import React, { useState, useCallback } from 'react';
import { useFacilityAvailability } from '../../hooks/useFacilityAvailability';
import { getAllFacilityIds, FACILITY_TYPES, getFacilityById } from '../../hooks/facilityConfig';
import { getAvailableTimeRanges, formatTimeRange, formatDateDisplay } from '../../hooks/availabilityUtils';
import FacilityFilters from './FacilityFilters';
import { getFilteredFacilitiesWithStatus, countFilteredFacilities } from '../../utils/facilityFilterUtils';
import MonthlyCalendarView from './MonthlyCalendarView';
import TodayAvailabilityGrid from './TodayAvailabilityGrid';
import GuestRoomCalendarView from './GuestRoomCalendarView';
import './FacilityAvailabilityDashboard.css';

const FacilityAvailabilityDashboard = () => {
  // View types
  const VIEW_TYPES = {
    TODAY: 'today',
    WEEK: 'week',
    MONTH: 'month'
  };

  // State management
  const [selectedView, setSelectedView] = useState(VIEW_TYPES.TODAY);
  const [filters, setFilters] = useState({
    categories: [],
    searchTerm: '',
    timeRanges: [],
    showAvailableOnly: false
  });
  
  // Determine the number of days based on view
  const getDaysForView = (view) => {
    switch (view) {
      case VIEW_TYPES.TODAY:
        return 1;
      case VIEW_TYPES.WEEK:
        return 7;
      case VIEW_TYPES.MONTH:
        return 30;
      default:
        return 1;
    }
  };
  
  // Use the hook to fetch facility availability
  const { data, loading, error, refetch } = useFacilityAvailability({
    facilityIds: getAllFacilityIds(),
    days: getDaysForView(selectedView),
    autoRefetch: true,
    refetchInterval: 5 * 60 * 1000 // 5 minutes
  });
  
  // Get filtered facilities
  const filteredFacilities = useCallback(() => {
    return getFilteredFacilitiesWithStatus(filters, data, selectedView);
  }, [filters, data, selectedView]);

  // Get facility counts
  const facilityCounts = useCallback(() => {
    return countFilteredFacilities(filters, data);
  }, [filters, data]);

  // Group filtered facilities by category
  const getFilteredFacilitiesByCategory = useCallback(() => {
    const filtered = filteredFacilities();
    const grouped = {};
    
    filtered.forEach(facility => {
      if (!grouped[facility.category]) {
        grouped[facility.category] = [];
      }
      grouped[facility.category].push(facility);
    });
    
    return grouped;
  }, [filteredFacilities]);

  // Handle filter changes
  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  // Get availability status for a facility on a specific day
  const getAvailabilityStatus = (facilityId, dayIndex = 0) => {
    if (!data || !data.facilities || !data.facilities[facilityId]) {
      return { status: 'unknown', message: 'データ取得中...' };
    }
    
    const facilityData = data.facilities[facilityId];
    if (facilityData.error) {
      return { status: 'error', message: 'エラー' };
    }
    
    if (!facilityData.days || facilityData.days.length <= dayIndex) {
      return { status: 'unknown', message: 'データなし' };
    }
    
    const facility = getFacilityById(facilityId);
    const day = facilityData.days[dayIndex];
    
    // Handle guest rooms (daily availability)
    if (facility && facility.type === FACILITY_TYPES.DAILY) {
      if (day.available || day.status === 'available') {
        return { status: 'available', message: '空室' };
      } else {
        return { status: 'unavailable', message: '満室' };
      }
    }
    
    // Handle hourly facilities
    const availableRanges = getAvailableTimeRanges(day.slots || []);
    
    if (availableRanges.length === 0) {
      return { status: 'unavailable', message: '予約済み' };
    } else if (availableRanges.length === 1 && 
               availableRanges[0].start === day.slots[0].time && 
               availableRanges[0].end === day.slots[day.slots.length - 1].time) {
      return { status: 'available', message: '空き' };
    } else {
      const rangeStrings = availableRanges.map(range => formatTimeRange(range));
      return { 
        status: 'partial', 
        message: rangeStrings.length > 2 
          ? `${rangeStrings.slice(0, 2).join(', ')}...`
          : rangeStrings.join(', ')
      };
    }
  };

  // Get today's date string
  const getTodayDateString = () => {
    const today = new Date();
    return formatDateDisplay(`${today.getFullYear()}/${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}`);
  };

  // Handle view change
  const handleViewChange = (viewType) => {
    setSelectedView(viewType);
  };

  // Render tab buttons
  const renderTabs = () => (
    <div className="facility-tabs">
      <button 
        className={`tab-button ${selectedView === VIEW_TYPES.TODAY ? 'active' : ''}`}
        onClick={() => handleViewChange(VIEW_TYPES.TODAY)}
      >
        今日
      </button>
      <button 
        className={`tab-button ${selectedView === VIEW_TYPES.WEEK ? 'active' : ''}`}
        onClick={() => handleViewChange(VIEW_TYPES.WEEK)}
      >
        週間
      </button>
      <button 
        className={`tab-button ${selectedView === VIEW_TYPES.MONTH ? 'active' : ''}`}
        onClick={() => handleViewChange(VIEW_TYPES.MONTH)}
      >
        月間
      </button>
    </div>
  );

  // Render loading state
  const renderLoading = () => (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <p>施設の空き状況を読み込んでいます...</p>
    </div>
  );

  // Render error state
  const renderError = () => (
    <div className="error-container">
      <p className="error-message">{error}</p>
      <button className="retry-button" onClick={refetch}>
        再試行
      </button>
    </div>
  );

  // Render facility card
  const renderFacilityCard = (facility) => {
    const { status, message } = getAvailabilityStatus(facility.id);
    
    return (
      <div key={facility.id} className="facility-card">
        <div className="facility-header">
          <span className="facility-icon">{facility.icon}</span>
          <h4 className="facility-name">{facility.shortName}</h4>
        </div>
        <div className="facility-hours">
          {facility.type === 'hourly' 
            ? `${facility.openTime} - ${facility.closeTime}`
            : `チェックイン ${facility.checkInTime} / チェックアウト ${facility.checkOutTime}`
          }
        </div>
        <div className={`facility-status status-${status}`}>
          <span className="status-indicator"></span>
          <span className="status-text">{message}</span>
        </div>
      </div>
    );
  };

  // Render content based on selected view
  const renderContent = () => {
    if (loading) return renderLoading();
    if (error) return renderError();

    return (
      <div className="facility-content">
        <div className="content-header">
          <h3 className="view-title">
            {selectedView === VIEW_TYPES.TODAY && `本日の空き状況 (${getTodayDateString()})`}
            {selectedView === VIEW_TYPES.WEEK && '今週の空き状況'}
            {selectedView === VIEW_TYPES.MONTH && '今月の空き状況'}
          </h3>
          <button className="refresh-button" onClick={refetch} disabled={loading}>
            🔄 更新
          </button>
        </div>
        
        {selectedView === VIEW_TYPES.TODAY ? (
          // Today view - use TodayAvailabilityGrid component
          <>
            <TodayAvailabilityGrid />
            
            {/* Guest Rooms Section */}
            <div className="guest-rooms-section">
              <h3 className="section-title">ゲストルーム</h3>
              <div className="guest-rooms-grid">
                {['guestroom-luz', 'guestroom-madera'].map(roomId => {
                  const facility = getFacilityById(roomId);
                  const { status, message } = getAvailabilityStatus(roomId);
                  
                  return (
                    <div key={roomId} className="facility-card guest-room-card">
                      <div className="facility-header">
                        <span className="facility-icon">{facility.icon}</span>
                        <h4 className="facility-name">{facility.name}</h4>
                      </div>
                      <div className="facility-hours">
                        チェックイン {facility.checkInTime} / チェックアウト {facility.checkOutTime}
                      </div>
                      <div className={`facility-status status-${status}`}>
                        <span className="status-indicator"></span>
                        <span className="status-text">{message}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : selectedView === VIEW_TYPES.MONTH ? (
          // Month view - show calendar for guest rooms
          <>
            <MonthlyCalendarView />
            
            {/* Guest Rooms Calendar Section */}
            <div className="guest-rooms-section">
              <h3 className="section-title">ゲストルーム予約カレンダー</h3>
              <div className="guest-rooms-calendars">
                <GuestRoomCalendarView facilityId="guestroom-luz" />
                <GuestRoomCalendarView facilityId="guestroom-madera" />
              </div>
            </div>
          </>
        ) : (
          // Week view - show summary
          <div className="facilities-summary">
            <p className="summary-note">
              今後7日間の空き状況概要
            </p>
            <div className="facilities-grid">
              {filteredFacilities().map(facility => {
                const facilityData = data?.facilities?.[facility.id];
                let availableDays = 0;
                
                // Handle guest rooms differently
                if (facility.type === FACILITY_TYPES.DAILY) {
                  // For guest rooms, only count if we have actual data with available status
                  if (!facilityData?.days || facilityData.days.length === 0) {
                    availableDays = 0;
                  } else {
                    availableDays = facilityData.days.filter(day => 
                      day.available || day.status === 'available'
                    ).length || 0;
                  }
                } else {
                  // Handle hourly facilities
                  availableDays = facilityData?.days?.filter(day => {
                    if (!day.slots || day.slots.length === 0) return false;
                    
                    // Check if there are any booked slots
                    const bookedSlots = day.slots.filter(slot => slot.status === 'booked');
                    const pastDeadlineSlots = day.slots.filter(slot => slot.status === 'past-deadline');
                    
                    // All slots are past deadline - check if any would have been available
                    if (pastDeadlineSlots.length === day.slots.length) {
                      return day.slots.some(slot => slot.available === true);
                    }
                    
                    // If all active slots are booked, not available
                    const activeSlotsCount = day.slots.filter(s => s.status !== 'past-deadline').length;
                    if (bookedSlots.length === activeSlotsCount && activeSlotsCount > 0) {
                      return false;
                    }
                    
                    // Otherwise, it's at least partially available
                    return true;
                  }).length || 0;
                }
                
                const totalDays = facilityData?.days?.length || getDaysForView(selectedView);
                
                return (
                  <div key={facility.id} className="facility-card">
                    <div className="facility-header">
                      <span className="facility-icon">{facility.icon}</span>
                      <h4 className="facility-name">{facility.shortName}</h4>
                    </div>
                    <div className="availability-summary">
                      <div className="availability-bar">
                        <div 
                          className="availability-fill"
                          style={{ width: `${(availableDays / totalDays) * 100}%` }}
                        />
                      </div>
                      <span className="availability-text">
                        {facility.type === FACILITY_TYPES.DAILY && (!facilityData?.days || facilityData.days.length === 0) ? 
                          '予約状況不明' : 
                          `${availableDays}/${totalDays}日 ${facility.type === FACILITY_TYPES.DAILY ? '空室' : '利用可能'}`
                        }
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="facility-availability-dashboard">
      <div className="dashboard-header">
        <h2 className="dashboard-title">施設空き状況</h2>
        <p className="dashboard-subtitle">各施設のリアルタイム空き状況を確認</p>
      </div>
      
      <FacilityFilters 
        onFiltersChange={handleFiltersChange}
        filteredCount={facilityCounts().filtered}
        totalCount={facilityCounts().total}
      />
      
      {renderTabs()}
      {renderContent()}
    </div>
  );
};

export default FacilityAvailabilityDashboard;